import { useState, useEffect, useCallback, memo, useMemo } from "react";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { PrimaryButton } from "../PrimaryButton/PrimaryButton";
import SearchField from "../SearchField/SearchField";
import { StampLabel } from "../StampLabel/StampLabel";
import toast from "react-hot-toast";
import withAuthProvider from "../../services/withAuthProvider";
import MoreButton from "../MoreButton/MoreButton";
import { defaultImageCar } from "../../utils/defaultValue";
import { locDateFormatter } from "../../utils/string";
import { SkeletonFavorite, SkeletonSearch } from "../Skeleton/Skeleton";
import { useHistory } from "react-router-dom";
import ModalLayout from "../../containers/Layouts/ModalLayout/ModalLayout";
import Share from "../Modals/Share";

const ChildFavorit = memo(({ data, handleDelete, handleLiveAuction, handleShare }) => {
  const lot_number = data?.stock_unit?.lot_number;
  const lane_name = data?.stock_unit?.auction?.auction_lane_name;
  return (
    <div className="card rounded-custom overflow-hidden">
      <div className="row">
        <div className="col-md-3 pe-md-0 position-relative fav-img-container">
          <span className="position-absolute top-0 end-0 py-1 px-2 fw-semibold mt-2 me-4 me-md-2 font-sm text-primary bg-light rounded">
            Grade {data?.stock_unit?.unit_inspection?.grade || "-"}
          </span>
          <img
            src={data?.stock_unit?.pictures[0]?.image_url || defaultImageCar}
            style={{ objectFit: "cover" }}
            className="w-100 h-100"
            alt=""
          />
        </div>
        <div className="col-md-6 ps-md-0">
          <StampLabel text={lot_number && lane_name ? `LOT-${lane_name}${lot_number}` : "-"} />
          <div className="p-2">
            <div className="border-bottom py-2">
              <div className="text-secondary fw-bold mb-3">{data?.stock_unit?.unit_name || "-"}</div>
              <div>
                {data?.stock_unit?.tags?.map((el, idx) => (
                  <span className="value me-1" key={idx}>
                    <span className="badge-status secondary">{el?.name || "-"}</span>
                  </span>
                ))}
              </div>
              <div className="fw-bold">
                Rp. {data?.stock_unit?.base_price ? data?.stock_unit?.base_price?.toLocaleString() : "-"}
              </div>
            </div>
            <div className="d-flex font-xs align-items-center my-2 justify-content-between">
              <div className="text-secondary fw-semibold">
                {data?.stock_unit?.location?.name &&
                data?.stock_unit?.auction?.date_start &&
                data?.stock_unit?.auction?.time_start
                  ? locDateFormatter(
                      data?.stock_unit?.location?.name,
                      new Date(data?.stock_unit?.auction?.date_start),
                      data?.stock_unit?.auction?.time_start
                    )
                  : ""}
              </div>
              {/* <div className="border rounded px-2 border-primary text-primary">
                                Live
                            </div> */}
            </div>
          </div>
        </div>
        <div className="col-md-3 py-3 px-4">
          <div className="d-flex flex-column align-items-start fw-semibold font-sm">
            <PrimaryButton className="w-100 fw-semibold" onClick={handleLiveAuction}>
              Live Auction
            </PrimaryButton>
            <div className="pt-2 text-center w-100 cursor-pointer hover-primary disable-copy" onClick={handleDelete}>
              Hapus Wishlist
            </div>
            <div className="pt-2 text-center w-100 cursor-pointer hover-primary disable-copy" onClick={handleShare}>
              Bagikan
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

const FavoritContent = () => {
  const [search, setSearch] = useState("");
  const [isOpenShare, setIsOpenShare] = useState(false);
  const { push } = useHistory();
  const [detail, setDetail] = useState([]);
  const [favorite, setFavorite] = useState();

  const closeModalShare = () => {
    setIsOpenShare(false);
  };

  const handleChangeSearch = (e) => {
    const { value } = e.target;
    setSearch(value);
  };

  const queryClient = useQueryClient();

  const { data, fetchNextPage, hasNextPage, isLoading } = useInfiniteQuery(
    ["favorites", search],
    async ({ pageParam = 1 }) => {
      const params = {
        page: pageParam,
        page_size: 5,
        search: search.length >= 3 ? search : undefined,
      };
      // Only send search if length >= 3 or empty (though generic check usually handles empty)
      // Original logic only fetched if search >= 3 or search === 0 length.
      // If 1 or 2 chars, it didn't fetch?
      // "if (search.length >= 3) ... if (search.length === 0)" in original useEffect.
      // We can handle that by disabling query if search length is 1 or 2.

      const response = await withAuthProvider.getDataWithAuth(`api/favorite`, params);
      if (response?.status === 200) {
        return response.data.data;
      }
      throw new Error("Failed to fetch favorites");
    },
    {
      getNextPageParam: (lastPage) => {
        if (lastPage.current_page < lastPage.last_page) {
          return lastPage.current_page + 1;
        }
        return undefined;
      },
      enabled: search.length === 0 || search.length >= 3,
    }
  );

  const favorites = useMemo(() => data?.pages.flatMap((page) => page.data) || [], [data]);

  const deleteMutation = useMutation(
    async (id) => {
      const response = await withAuthProvider.deleteDataWithAuth(`api/favorite/${id}/delete`);
      if (response?.status !== 200) {
        throw new Error("Failed to delete");
      }
      return response.data;
    },
    {
      onMutate: async (id) => {
        await queryClient.cancelQueries(["favorites", search]);
        const previousData = queryClient.getQueryData(["favorites", search]);

        queryClient.setQueryData(["favorites", search], (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              data: page.data.filter((item) => item.stock_unit_id !== id),
            })),
          };
        });

        return { previousData };
      },
      onError: (err, id, context) => {
        queryClient.setQueryData(["favorites", search], context.previousData);
        toast.error("Data gagal dihapus", {
          id: "error-message",
          duration: 3000,
        });
      },
      onSuccess: (data) => {
        toast.success(data.message || "Berhasil dihapus", {
          id: "success-message",
          duration: 3000,
        });
      },
      // Refetch is optional here since we updated optimistically, but good for consistency
      // onSettled: () => {
      //   queryClient.invalidateQueries(["favorites", search]);
      // }
    }
  );

  const removeFavorite = useCallback(
    (id) => {
      const loading = toast.loading("Mengirimkan...");
      deleteMutation.mutate(id, {
        onSettled: () => toast.dismiss(loading),
      });
    },
    [deleteMutation]
  );

  useEffect(() => {
    const data = favorites.find((data) => data.stock_unit_id === parseInt(favorite));
    if (data) {
      setDetail(data);
    }
  }, [favorite, favorites]);

  return (
    <>
      <div className="tab-content-caready border-bottom">
        <h3 className="title mb-0">Favorit</h3>
      </div>
      <div className="tab-content-caready">
        {isLoading ? (
          <SkeletonSearch />
        ) : (
          <SearchField placeholder="Cari di Favorit" value={search} name="search" onChange={handleChangeSearch} />
        )}
        <div className="mt-3 d-flex overflow-auto flex-column" style={{ gap: "1rem", maxHeight: "600px" }}>
          {isLoading
            ? new Array(5).fill(0).map((el, idx) => (
                <div className="card rounded-custom " key={`fav-${idx}`}>
                  <SkeletonFavorite />
                </div>
              ))
            : favorites.map((favorite, idx) => (
                <div key={idx}>
                  <ChildFavorit
                    data={favorite}
                    handleDelete={() => removeFavorite(favorite?.stock_unit_id)}
                    handleLiveAuction={() => push(`/live-auction/${favorite?.stock_unit?.auction?.id}`)}
                    handleShare={() => {
                      setFavorite(favorite?.stock_unit_id);
                      setIsOpenShare(true);
                    }}
                  />
                </div>
              ))}
          {favorites.length === 0 && isLoading === false && (
            <div className="px-3 py-2 font-sm text-center text-secondary w-100">Tidak ada Favorit</div>
          )}
        </div>
        {hasNextPage && <MoreButton onClick={() => fetchNextPage()} />}
      </div>
      <ModalLayout show={isOpenShare} handleClose={closeModalShare} title={"Bagikan"} className="modal-detail-unit">
        <Share shareUrl={window.location.origin + "/unit-lelang/" + detail?.stock_unit?.id} />
      </ModalLayout>
    </>
  );
};

export default FavoritContent;
